import { useMemo, useState } from "react";
import { Trash2, Plus, ArrowLeftRight, ArrowUp, ArrowDown, X } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import AiFlagToggle from "@/components/admin/AiFlagToggle";
import { api, BACKEND_URL } from "@/lib/auth";
import { mediaThumb } from "@/lib/mediaThumb";

type Media = {
    id: string;
    title: string;
    media_type: string;
    mime: string;
    poster_filename?: string | null;
    poster_url?: string | null;
    stored_url?: string | null;
    original_filename?: string;
    alt?: string;
    width?: number | null;
    height?: number | null;
};

export type Pair = {
    id: string;
    before_media_id: string | null;
    after_media_id: string | null;
    title: string | null;
    sort_order: number;
    before_url?: string | null;
    after_url?: string | null;
};

type Project = {
    id: string;
    pairs?: Pair[];
};

type Props = {
    project: Project;
    availableMedia: Media[];
    onReload: () => Promise<void>;
};

/**
 * Editor für Vorher/Nachher-Paare auf Projekt-Ebene.
 * Nutzt die neuen Backend-Endpoints:
 *   POST   /api/admin/projects/{id}/pairs
 *   PATCH  /api/admin/projects/{id}/pairs/{pair_id}
 *   DELETE /api/admin/projects/{id}/pairs/{pair_id}
 */
export default function PairsEditor({ project, availableMedia, onReload }: Props) {
    const [picker, setPicker] = useState<{ pairId: string; role: "before" | "after" } | null>(null);
    const [busy, setBusy] = useState(false);

    // Nur Fotos anbieten (keine Videos)
    const photoMedia = useMemo(
        () => availableMedia.filter((m) => (m.mime || "").startsWith("image/")),
        [availableMedia],
    );
    const mediaById = useMemo(() => {
        const map: Record<string, Media> = {};
        for (const m of availableMedia) map[m.id] = m;
        return map;
    }, [availableMedia]);

    const pairs = [...(project.pairs || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
    );

    const addPair = async () => {
        setBusy(true);
        await api(`/api/admin/projects/${project.id}/pairs`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sort_order: pairs.length }),
        });
        await onReload();
        setBusy(false);
    };

    const patchPair = async (pairId: string, updates: Partial<Pair>) => {
        setBusy(true);
        await api(`/api/admin/projects/${project.id}/pairs/${pairId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updates),
        });
        await onReload();
        setBusy(false);
    };

    const deletePair = async (pairId: string) => {
        if (!window.confirm("Paar wirklich löschen?")) return;
        setBusy(true);
        await api(`/api/admin/projects/${project.id}/pairs/${pairId}`, { method: "DELETE" });
        await onReload();
        setBusy(false);
    };

    const move = async (pairId: string, delta: -1 | 1) => {
        const idx = pairs.findIndex((p) => p.id === pairId);
        const j = idx + delta;
        if (idx < 0 || j < 0 || j >= pairs.length) return;
        // Swap sort_order
        const a = pairs[idx];
        const b = pairs[j];
        setBusy(true);
        await Promise.all([
            api(`/api/admin/projects/${project.id}/pairs/${a.id}`, {
                method: "PATCH", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sort_order: b.sort_order || j }),
            }),
            api(`/api/admin/projects/${project.id}/pairs/${b.id}`, {
                method: "PATCH", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sort_order: a.sort_order || idx }),
            }),
        ]);
        await onReload();
        setBusy(false);
    };

    const chooseMedia = async (mediaId: string) => {
        if (!picker) return;
        const upd: Partial<Pair> = picker.role === "before"
            ? { before_media_id: mediaId }
            : { after_media_id: mediaId };
        await patchPair(picker.pairId, upd);
        setPicker(null);
    };

    const removeMedia = async (pairId: string, role: "before" | "after") => {
        const upd: Partial<Pair> = role === "before"
            ? { before_media_id: null }
            : { after_media_id: null };
        await patchPair(pairId, upd);
    };

    return (
        <div className="mt-10" data-testid="projekt-pairs-section">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <div className="hf-eyebrow">Vorher · Nachher</div>
                    <h3 className="font-display text-2xl mt-2">
                        {pairs.length} Paar{pairs.length === 1 ? "" : "e"}
                    </h3>
                    <p className="text-xs text-hf-muted mt-1">
                        Nur vollständige Paare (beide Bilder gesetzt) werden auf der Public-Seite gezeigt.
                    </p>
                    <p className="text-xs mt-2 max-w-2xl" style={{ color: "hsl(var(--hf-ink))" }} data-testid="pair-hint">
                        <strong>Wichtig für den Slider-Effekt:</strong> Vorher- und Nachher-Bild müssen aus exakt derselben Kameraposition und mit demselben Bildausschnitt aufgenommen sein. Andernfalls entstehen beim Verschieben des Reglers Sprünge im Bild.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => void addPair()}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-50"
                    style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}
                    data-testid="pair-add-btn"
                >
                    <Plus size={14} /> Neues Paar hinzufügen
                </button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5">
                {pairs.map((pair, i) => {
                    const beforeM = pair.before_media_id ? mediaById[pair.before_media_id] : null;
                    const afterM = pair.after_media_id ? mediaById[pair.after_media_id] : null;
                    const complete = !!(beforeM && afterM);
                    const ratioMismatch = (() => {
                        if (!complete || !beforeM || !afterM) return null;
                        const rb = (beforeM.width || 0) / (beforeM.height || 1);
                        const ra = (afterM.width || 0) / (afterM.height || 1);
                        if (!rb || !ra) return null;
                        const diff = Math.abs(rb - ra) / Math.max(rb, ra);
                        return diff > 0.03 ? { diffPct: Math.round(diff * 100), rb, ra } : null;
                    })();
                    const beforeUrl = pair.before_url || beforeM?.stored_url || null;
                    const afterUrl = pair.after_url || afterM?.stored_url || null;
                    return (
                        <div
                            key={pair.id}
                            className="p-4 rounded-sm"
                            style={{
                                background: "hsl(var(--hf-surface))",
                                border: "1px solid hsl(var(--hf-line))",
                            }}
                            data-testid={`pair-card-${pair.id}`}
                        >
                            {/* Header: Titel + Aktionen */}
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <input
                                    type="text"
                                    defaultValue={pair.title || ""}
                                    placeholder="Titel des Paares (z.B. Wohnzimmer)"
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v !== (pair.title || "")) void patchPair(pair.id, { title: v || null });
                                    }}
                                    className="flex-1 min-w-[200px] px-3 py-1.5 border rounded-sm bg-white text-sm"
                                    style={{ borderColor: "hsl(var(--hf-line))" }}
                                    data-testid={`pair-title-${pair.id}`}
                                />
                                {!complete && (
                                    <span
                                        className="text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-sm"
                                        style={{
                                            background: "hsl(var(--hf-warn, 45 90% 55%) / 0.15)",
                                            color: "hsl(var(--hf-warn, 45 90% 40%))",
                                            border: "1px solid hsl(var(--hf-warn, 45 90% 55%) / 0.4)",
                                        }}
                                        data-testid={`pair-incomplete-badge-${pair.id}`}
                                    >
                                        Unvollständig — beide Bilder erforderlich
                                    </span>
                                )}
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        disabled={i === 0 || busy}
                                        onClick={() => void move(pair.id, -1)}
                                        className="p-1.5 rounded-sm disabled:opacity-30"
                                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                                        title="Nach oben"
                                        data-testid={`pair-up-${pair.id}`}
                                    >
                                        <ArrowUp size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        disabled={i === pairs.length - 1 || busy}
                                        onClick={() => void move(pair.id, 1)}
                                        className="p-1.5 rounded-sm disabled:opacity-30"
                                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                                        title="Nach unten"
                                        data-testid={`pair-down-${pair.id}`}
                                    >
                                        <ArrowDown size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void deletePair(pair.id)}
                                        disabled={busy}
                                        className="p-1.5 rounded-sm ml-1"
                                        style={{
                                            border: "1px solid hsl(var(--hf-danger) / 0.4)",
                                            color: "hsl(var(--hf-danger))",
                                        }}
                                        title="Paar löschen"
                                        data-testid={`pair-delete-${pair.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Slot-Ebene */}
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                                {/* Before Slot */}
                                <PairSlot
                                    label="Vorher"
                                    media={beforeM}
                                    onOpen={() => setPicker({ pairId: pair.id, role: "before" })}
                                    onRemove={() => void removeMedia(pair.id, "before")}
                                    testid={`pair-before-slot-${pair.id}`}
                                />
                                <div className="hidden md:flex items-center justify-center text-hf-muted">
                                    <ArrowLeftRight size={22} style={{ color: "hsl(var(--hf-gold))" }} />
                                </div>
                                {/* After Slot */}
                                <PairSlot
                                    label="Nachher"
                                    media={afterM}
                                    onOpen={() => setPicker({ pairId: pair.id, role: "after" })}
                                    onRemove={() => void removeMedia(pair.id, "after")}
                                    testid={`pair-after-slot-${pair.id}`}
                                />
                            </div>

                            {/* Mini-Preview des vollständigen Sliders */}
                            {complete && beforeUrl && afterUrl && (
                                <div className="mt-4" data-testid={`pair-preview-${pair.id}`}>
                                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Vorschau</div>
                                    {ratioMismatch && (
                                        <div
                                            className="mt-2 mb-2 px-3 py-2 text-xs rounded-sm"
                                            style={{
                                                background: "hsl(45 90% 55% / 0.14)",
                                                color: "hsl(35 65% 30%)",
                                                border: "1px solid hsl(45 90% 55% / 0.5)",
                                            }}
                                            data-testid={`pair-ratio-warn-${pair.id}`}
                                        >
                                            Seitenverhältnis unterscheidet sich um {ratioMismatch.diffPct} % — Slider könnte unpassend wirken.
                                        </div>
                                    )}
                                    <div className="mt-2 max-w-md">
                                        <BeforeAfterSlider
                                            beforeUrl={beforeUrl}
                                            afterUrl={afterUrl}
                                            aspectRatio="3 / 2"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {pairs.length === 0 && (
                    <div
                        className="p-8 rounded-sm text-center text-hf-muted text-sm"
                        style={{
                            background: "hsl(var(--hf-surface))",
                            border: "1px dashed hsl(var(--hf-line))",
                        }}
                    >
                        Noch keine Vorher/Nachher-Paare. Klicken Sie auf {"„Neues Paar hinzufügen\""}.
                    </div>
                )}
            </div>

            {/* Media-Picker Modal */}
            {picker && (
                <div
                    className="fixed inset-0 z-[220] flex items-center justify-center p-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.6)" }}
                    onClick={() => setPicker(null)}
                    data-testid="pair-picker-modal"
                >
                    <div
                        className="w-full max-w-4xl max-h-[80vh] overflow-auto rounded-sm p-5"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="hf-eyebrow">Bild wählen</div>
                                <h4 className="font-display text-xl mt-1">
                                    {picker.role === "before" ? "Vorher-Bild" : "Nachher-Bild"}
                                </h4>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPicker(null)}
                                className="p-1.5 rounded-sm"
                                style={{ border: "1px solid hsl(var(--hf-line))" }}
                                data-testid="pair-picker-close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {photoMedia.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => void chooseMedia(m.id)}
                                    className="text-left rounded-sm overflow-hidden hover:ring-2 focus:ring-2 focus:outline-none"
                                    style={{
                                        border: "1px solid hsl(var(--hf-line))",
                                    }}
                                    data-testid={`pair-picker-item-${m.id}`}
                                >
                                    <div className="aspect-[3/2] bg-black">
                                        <img
                                            src={mediaThumb(m, BACKEND_URL) || ""}
                                            alt={m.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="px-2 py-1.5 text-xs truncate">{m.title || m.original_filename}</div>
                                </button>
                            ))}
                            {photoMedia.length === 0 && (
                                <div className="col-span-full text-center text-hf-muted text-sm py-10">
                                    Keine Fotos verfügbar. Laden Sie unter Admin/Medien Bilder hoch.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PairSlot(
    { label, media, onOpen, onRemove, testid }:
    {
        label: string;
        media: Media | null;
        onOpen: () => void;
        onRemove: () => void;
        testid: string;
    },
) {
    return (
        <div
            className="relative aspect-[3/2] rounded-sm overflow-hidden"
            style={{
                background: "hsl(var(--hf-bg))",
                border: media ? "1px solid hsl(var(--hf-line))" : "1px dashed hsl(var(--hf-line))",
            }}
            data-testid={testid}
        >
            {media ? (
                <>
                    <img
                        src={mediaThumb(media, BACKEND_URL) || ""}
                        alt={media.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2">
                        <span
                            className="px-2 py-1 text-[10px] tracking-[0.18em] uppercase rounded-sm"
                            style={{
                                background: "hsl(var(--hf-ink) / 0.78)",
                                color: "hsl(var(--hf-gold-soft))",
                            }}
                        >
                            {label}
                        </span>
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1">
                        <button
                            type="button"
                            onClick={onOpen}
                            className="px-2 py-1 text-[10px] rounded-sm"
                            style={{ background: "hsl(var(--hf-bg))", color: "hsl(var(--hf-ink))" }}
                        >
                            Ändern
                        </button>
                        <button
                            type="button"
                            onClick={onRemove}
                            className="p-1 rounded-sm"
                            style={{ background: "hsl(var(--hf-bg))", color: "hsl(var(--hf-danger))" }}
                            title="Entfernen"
                        >
                            <X size={12} />
                        </button>
                    </div>
                    <div className="absolute bottom-2 left-2 z-10">
                        <AiFlagToggle
                            mediaId={media.id}
                            compact
                            testIdPrefix={`${testid}-ai-flag`}
                        />
                    </div>
                </>
            ) : (
                <button
                    type="button"
                    onClick={onOpen}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-hf-muted hover:text-hf-ink transition-colors"
                >
                    <Plus size={22} />
                    <span className="text-xs tracking-[0.18em] uppercase">{label} wählen</span>
                </button>
            )}
        </div>
    );
}
